import { finishBrowserHydration } from "~/lib/ssr/browser-hydration";
import { THEME_BOOTSTRAP_SCRIPT } from "~/lib/theme-bootstrap";
import { createDocumentHead } from "~/lib/ssr/document-head";
import { getDocumentMetadata } from "~/lib/ssr/document-metadata";
import {
  HeadContent,
  Scripts,
  ScriptOnce,
  Outlet,
  createRootRouteWithContext,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useEffect, type ReactNode } from "react";
import { LazyMotion, MotionConfig } from "framer-motion";
import { useRouter } from "@tanstack/react-router";
import { ResolvedAppProviders } from "~/app-bootstrap";
import { RootErrorBoundary } from "~/components/root-error-boundary";
import { ThemeProvider } from "~/components/theme-provider";
import { getActiveLocale } from "~/lib/language-lens";
import { initializeRouteVisit } from "~/lib/route-visit";
import { initSentry } from "~/lib/sentry";
import appCss from "~/styles/app.css?url";
import { DefaultCatchBoundary } from "~/components/default-catch-boundary";
import { NotFound } from "~/components/not-found";
import { RouterDocumentTitleSync } from "~/lib/router-document-title";
import type { AppRouterContext } from "~/lib/router-runtime";
import { THEME_CHROME_COLORS } from "~/lib/theme-chrome";

const loadMotionFeatures = () =>
  import("~/lib/framer-motion-features").then((module) => module.default);

export const Route = createRootRouteWithContext<AppRouterContext>()({
  ssr: true,
  shellComponent: RootDocument,
  errorComponent: DefaultCatchBoundary,
  loader: () => getDocumentMetadata() ?? null,
  head: ({ loaderData }) => ({
    links: [
      ...createDocumentHead(loaderData, getActiveLocale()).links,
      { rel: "stylesheet", href: appCss },
      {
        rel: "preload",
        href: "/fonts/mori/mori-variable.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      {
        href: "/favicon-48x48.png",
        rel: "alternate icon",
        sizes: "48x48",
        type: "image/png",
      },
      {
        href: "/favicon.svg",
        rel: "icon",
        type: "image/svg+xml",
      },
      {
        href: "/apple-touch-icon.png",
        rel: "apple-touch-icon",
        sizes: "180x180",
      },
      {
        href: "/site.webmanifest",
        rel: "manifest",
      },
      {
        color: THEME_CHROME_COLORS.light,
        href: "/safari-pinned-tab.svg",
        rel: "mask-icon",
      },
    ],
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      {
        name: "glider-release-sha",
        content: import.meta.env.VITE_DEPLOYMENT_SHA ?? "development",
      },
      ...createDocumentHead(loaderData, getActiveLocale()).meta,
      {
        content: "Glider.fi",
        name: "apple-mobile-web-app-title",
      },
      {
        content: THEME_CHROME_COLORS.light,
        name: "msapplication-TileColor",
      },
      {
        content: THEME_CHROME_COLORS.light,
        name: "theme-color",
      },
    ],
  }),
  notFoundComponent: () => <NotFound />,
  component: RootComponent,
});

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang={getActiveLocale()} suppressHydrationWarning>
      <head>
        <HeadContent />
        <ScriptOnce>{THEME_BOOTSTRAP_SCRIPT}</ScriptOnce>
      </head>
      <body>
        <div id="app">{children}</div>
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const router = useRouter();
  useEffect(() => {
    finishBrowserHydration();
    initializeRouteVisit(router);
    initSentry(router);
  }, [router]);
  return (
    <RootErrorBoundary>
      <ThemeProvider>
        <LazyMotion features={loadMotionFeatures} strict>
          <MotionConfig reducedMotion="user">
            <ResolvedAppProviders>
              <RouterDocumentTitleSync />
              <Outlet />
              {import.meta.env.DEV &&
              import.meta.env.VITE_ENABLE_ROUTER_DEVTOOLS === "1" ? (
                <TanStackRouterDevtools position="bottom-right" />
              ) : null}
            </ResolvedAppProviders>
          </MotionConfig>
        </LazyMotion>
      </ThemeProvider>
    </RootErrorBoundary>
  );
}
